import { Box, Chip, Typography } from "@mui/material";
import { RouteSelectorProps } from './RoutePreview.types'
import React from "react";

const METERS_TO_MILES = 0.000621371;

const RouteSelector: React.FC<RouteSelectorProps> = ({
  routes,
  selectedId,
  onSelect
}) => {

  const selected =
    routes.find((r) => r.id === selectedId) ?? null;

  const formatDistance = (m: number) =>
    `${(m * METERS_TO_MILES).toFixed(2)} mi`;

  const formatDuration = (s: number) => {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);

    return h > 0
      ? `${h}h ${m} min`
      : `${m} min`;
  };

  return (
    <Box>

      <Typography
        variant="overline"

        sx={{
          color: "text.primary",
          letterSpacing: "0.08em",
          mb: 1.5,
          display: "block"
        }}
      >
        Choose your route
      </Typography>

      {/* ROUTE OPTIONS */}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.25,
          mb: 1.5
        }}
      >
        {routes.map((route) => {

          const isSelected =
            route.id === selectedId;

          const hasToll =
            route.toll > 0;

          return (
            <Box
              key={route.id}

              onClick={() => onSelect(route)}

              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.75,
                px: 2,
                py: 1.75,
                border:
                  isSelected
                    ? "2px solid"
                    : "1px solid",

                borderColor:
                  isSelected
                    ? "text.primary"
                    : "divider",

                borderRadius: 2,
                cursor: "pointer",
                bgcolor: "background.paper",
                transition: "border-color 0.15s",
                "&:hover": {
                  borderColor: "text.secondary",
                },
              }}
            >

              {/* RADIO */}

              <Box
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "1.5px solid",
                  borderColor:
                    isSelected
                      ? "text.primary"
                      : "divider",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      bgcolor: "text.primary",
                    }}
                  />
                )}
              </Box>

              {/* COLOR BAR */}

              <Box
                sx={{
                  width: 3,
                  height: 36,
                  borderRadius: 1,
                  flexShrink: 0,
                  bgcolor:
                    hasToll
                      ? "#378ADD"
                      : "#3B6D11",
                }}
              />

              {/* ROUTE INFO */}
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    color: "text.secondary",
                    fontSize: {
                      xs: "1.25rem",
                      sm: "1.5rem",
                    },
                  }}
                >
                  {` $ ${route.totalCharge.toFixed(2)} · ${formatDistance(route.distanceMeters)} · ${formatDuration(route.durationSeconds)}`}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  <Chip
                    label={
                      hasToll
                        ? `Tolls · $${route.toll.toFixed(2)}`
                        : "No tolls"
                    }

                    size="medium"

                    sx={{
                      borderRadius: "999px",

                      fontSize: 11,

                      height: 22,

                      bgcolor:
                        hasToll
                          ? "#FCEBEB"
                          : "#EAF3DE",

                      color:
                        hasToll
                          ? "#A32D2D"
                          : "#3B6D11",
                    }}
                  />
                </Box>
              </Box>

              {/* TOLL */}

              <Box
                sx={{
                  textAlign: "right",

                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="body2"

                  sx={{
                    fontWeight: 500,

                    color:
                      hasToll
                        ? "#A32D2D"
                        : "text.disabled",
                  }}
                >
                  {hasToll
                    ? `$${route.toll.toFixed(2)}`
                    : "—"}
                </Typography>

                <Typography
                  variant="caption"

                  sx={{
                    color: "text.tertiary"
                  }}
                >
                  toll cost
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* SUMMARY */}

      {selected && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "repeat(2, 1fr)",
              sm: "repeat(4, 1fr)",
            },
            gap: 2,
            bgcolor: "action.hover",
            borderRadius: 2,
            px: 2,
            py: 1.5,
          }}
        >
          {[
            {
              label: "Price",
              value: `$ ${selected.totalCharge.toFixed(2)}`,
            },

            {
              label: "Distance",
              value: formatDistance(
                selected.distanceMeters
              ),
            },

            {
              label: "Est. time",
              value: formatDuration(
                selected.durationSeconds
              ),
            },

            {
              label: "Tolls",

              value:
                selected.toll > 0
                  ? `$${selected.toll.toFixed(2)}`
                  : "None",

              color:
                selected.toll > 0
                  ? "#ff0000"
                  : "#3B6D11",
            },

          ].map((item, i, arr) => (

            <React.Fragment key={item.label}>

              <Box
                sx={{
                  minWidth: 0,
                  pr: {
                    sm: 2,
                  },
                  borderRight: {
                    xs: "none",
                    sm:
                      i < arr.length - 1
                        ? "1px solid"
                        : "none",
                  },
                  borderColor: "divider",
                }}
              >
                <Typography
                  variant="caption"

                  sx={{
                    color: "text.disabled",
                    textTransform: "uppercase",
                    letterSpacing: "0.07em",
                    display: "block",
                    mb: 0.5,
                  }}
                >
                  {item.label}
                </Typography>

                <Typography
                  variant="body1"

                  sx={{
                    fontWeight: 500,

                    color:
                      item.color ??
                      "text.primary",
                  }}
                >
                  {item.value}
                </Typography>
              </Box>

            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default RouteSelector;